/**
 * Park Manager Portal Layout
 * Mobile-first layout for park managers with simplified navigation
 */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  ClipboardList, 
  AlertCircle, 
  Calendar,
  Menu,
  X,
  LogOut,
  Home,
  User
} from 'lucide-react';

const navItems = [
  { path: '/portal', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/portal/jobs', icon: ClipboardList, label: 'Jobs' },
  { path: '/portal/report', icon: AlertCircle, label: 'Report Issue' },
  { path: '/portal/ppm', icon: Calendar, label: 'PPM Schedule' },
];

export function PortalLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Header */}
      <header className="bg-[#1F7A78] text-white sticky top-0 z-50 md:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Home className="h-5 w-5" />
            <span className="font-semibold text-sm">Park Portal</span>
          </div>
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-white/10"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="px-4 pb-4 space-y-1 border-t border-white/10">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  location.pathname === item.path 
                    ? 'bg-white/20 font-medium' 
                    : 'hover:bg-white/10'
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            ))}
            <div className="pt-2 border-t border-white/10">
              <div className="flex items-center gap-2 px-3 py-2 text-sm opacity-80">
                <User className="h-4 w-4" />
                {user?.name || 'Park Manager'}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-white/10 w-full text-left text-red-200"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
          </nav>
        )}
      </header>

      {/* Desktop Layout */}
      <div className="hidden md:flex">
        {/* Desktop Sidebar */}
        <aside className="w-64 bg-[#1F7A78] text-white min-h-screen fixed left-0 top-0">
          <div className="p-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <Home className="h-6 w-6" />
              <div>
                <h1 className="font-bold">Park Portal</h1>
                <p className="text-xs opacity-70">Leisure Home Solutions</p>
              </div>
            </div>
          </div>
          
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  location.pathname === item.path 
                    ? 'bg-white/20 font-medium' 
                    : 'hover:bg-white/10'
                }`}
              >
                <item.icon className="h-4 w-4" />
                {item.label}
              </Link>
            ))}
          </nav>
          
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
            <div className="flex items-center gap-2 px-3 py-2 text-sm opacity-80 mb-2">
              <User className="h-4 w-4" />
              <span className="truncate">{user?.name || 'Park Manager'}</span>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-white hover:bg-white/10"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 ml-64 min-h-screen">
          {children}
        </main>
      </div>

      {/* Mobile Content */}
      <main className="md:hidden">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 md:hidden z-40">
        <div className="flex justify-around py-2">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center px-3 py-1 text-xs ${
                location.pathname === item.path 
                  ? 'text-[#1F7A78] font-medium' 
                  : 'text-slate-500'
              }`}
            >
              <item.icon className="h-5 w-5 mb-0.5" />
              <span>{item.label.split(' ')[0]}</span>
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default PortalLayout;
